import * as React from 'react';
import { useEffect, useState } from 'react';
import 'reactflow/dist/style.css';
import './lineageStyle.css';

import { Alert, Badge, SpaceBetween, Spinner } from '@amzn/awsui-components-react-v3';
import { getLatticeLineageForAsset } from 'src/api/latticelineage';
import {
  Background,
  BackgroundVariant,
  ControlButton,
  Controls,
  Panel,
  ReactFlow,
  useEdgesState,
  useNodesState,
} from 'reactflow';

import {
  getNodeIdsToBeIncludedInGraph,
  getNodesAndEdgesFromGetLineageGraph,
  nodeTypesCustom,
} from 'src/components/lineage/lineageUtil';
import { getLayoutedElements } from 'src/components/lineage/dagreHelper';
import { FaRedo } from 'react-icons/fa';
import { selectedLineageNodeComponent } from 'src/components/lineage/lineageComponents';
import { fetchFacetInfoForNodeId, getBaseNodeId } from 'src/components/lineage/common';

export interface LineageDetailProps {
  baseNodeId: string;
  emptyLineage: boolean;
  setEmptyLineage: any;
  loadingLineage: boolean;
  setLoadingLineage: any;
}

export const LineageDetail = (props: LineageDetailProps) => {
  const [loadingSelectedNode, setLoadingSelectedNode] = useState(true);

  const [lineageNodes, setLineageNodes] = useNodesState([]);
  const [lineageEdges, setLineageEdges] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState(undefined);
  const [activeNodeIds, setActiveNodeIds] = useState([props.baseNodeId]);

  const latticeLogo = require('./lineageImages/LatticeLogo.png');

  useEffect(() => {
    if (props.baseNodeId) {
      setActiveNodeIds([...activeNodeIds, props.baseNodeId]);
    }
  }, [props.baseNodeId]);

  useEffect(() => {
    if (selectedNode) {
      setActiveNodeIds([...activeNodeIds, selectedNode.id]);
    }
  }, [selectedNode]);

  useEffect(() => {
    if (activeNodeIds.length) {
      if (selectedNode) {
        fetchLineageForAsset(selectedNode.id, false).then(() => {
          setLoadingSelectedNode(false);
        });
      } else {
        fetchLineageForAsset(props.baseNodeId, true);
      }
    }
  }, [activeNodeIds.length || selectedNode]);

  const fetchLineageForAsset = async (nodeId: string, resetLineage: boolean) => {
    try {
      const getLineageResponseData = await getLatticeLineageForAsset(nodeId);
      const facetInfo = await fetchFacetInfoForNodeId(nodeId);
      await processAndSetLineage(getLineageResponseData.graph, resetLineage, nodeId, facetInfo);
    } catch (err) {
      console.log(`Failed to fetch lineage value for nodeId:${nodeId}. Reason:${err}`);
      props.setEmptyLineage(true);
    } finally {
      props.setLoadingLineage(false);
    }
  };

  const processAndSetLineage = async (
    lineageList: any[],
    resetLineage: boolean,
    currentNodeId: string,
    selectedNodeMetadata: any,
  ) => {
    let nodesToBeIncludedInGraph = getNodeIdsToBeIncludedInGraph(lineageList, activeNodeIds, currentNodeId);
    let nodesAndEdgesInGraph = getNodesAndEdgesFromGetLineageGraph(lineageList, nodesToBeIncludedInGraph);
    const nodesInGraph = nodesAndEdgesInGraph.nodesInGraph;
    const edgesInGraph = nodesAndEdgesInGraph.edgesInGraph;
    let layoutedNodes: any[];
    let layoutedEdges: any[];
    let currentNode = nodesInGraph.find((node) => node && node.id === currentNodeId);
    if (resetLineage) {
      const { positionedNodes, positionedEdges } = getLayoutedElements(nodesInGraph, edgesInGraph);
      layoutedNodes = positionedNodes;
      layoutedEdges = positionedEdges;
      // set base node on reset
      let baseNode = layoutedNodes.find((node) => node && node.id === props.baseNodeId);
      if (baseNode) {
        baseNode.data.isBaseNode = true;
      }
    } else {
      // including previous nodes and edges when state update
      let existingNodeIdList = lineageNodes.filter((node) => node).map((node) => node.id);
      let newNodesInGraph = nodesInGraph.filter((node) => !existingNodeIdList.includes(node.id));
      let existingEdgeIdList = lineageEdges.filter((edge) => edge).map((edge) => edge.id);
      let newEdgesInGraph = edgesInGraph.filter((edge) => !existingEdgeIdList.includes(edge.id));
      const { positionedNodes, positionedEdges } = getLayoutedElements(
        [...lineageNodes, ...newNodesInGraph],
        [...lineageEdges, ...newEdgesInGraph],
      );
      layoutedNodes = positionedNodes;
      layoutedEdges = positionedEdges;
    }

    // set metadata from initial current node
    if (currentNode) {
      let requiredNode = layoutedNodes.find((node) => node && node.id === currentNode.id);
      if (requiredNode) {
        requiredNode.data.inputNodeNames = currentNode.data?.inputNodeNames;
        requiredNode.data.outputNodeNames = currentNode.data?.outputNodeNames;
      }
    }
    // set metadata for selected node
    if (selectedNode) {
      let requiredNode = layoutedNodes.find((node) => node && node.id === selectedNode.id);
      if (requiredNode) {
        requiredNode.selected = true;
        requiredNode.data.facetInfo = selectedNodeMetadata;
      }
    }
    setLineageNodes(layoutedNodes);
    setLineageEdges(layoutedEdges);
  };

  const handleOnNodeDragProcess = (event, node) => {
    setLineageNodes((lineageNodes) =>
      lineageNodes.map((n) => (n.id === node.id ? { ...n, position: node.position } : n)),
    );
    event.stopPropagation();
  };

  const handleNodeClickProcess = async (event, node) => {
    node.selected = true;
    setLoadingSelectedNode(true);
    setSelectedNode(node);
    event.stopPropagation();
  };

  const handleOnPaneClickProcess = () => {
    setSelectedNode(undefined);
  };

  const closePanel = () => {
    setSelectedNode(undefined);
  };

  if (props.loadingLineage) {
    return <Spinner size='large' />;
  } else if (props.emptyLineage) {
    return (
      <Alert data-testid='lineage-alert' header='No Lineage Found'>
        Lineage information is not available for this dataset.
      </Alert>
    );
  } else {
    return (
      <div className='lineage-container'>
        <div className='lineage-react-flow-wrapper'>
          <ReactFlow
            nodes={lineageNodes}
            edges={lineageEdges}
            fitView
            nodeTypes={nodeTypesCustom}
            selectNodesOnDrag={false}
            nodesDraggable={true}
            elementsSelectable={true}
            nodesFocusable={true}
            onNodeClick={handleNodeClickProcess}
            onNodeDragStop={handleOnNodeDragProcess}
            onPaneClick={handleOnPaneClickProcess}
            proOptions={{ hideAttribution: true }}
          >
            <Background color='#ccc' variant={BackgroundVariant.Dots} size={2} />
            <Controls position='top-right'>
              <ControlButton onClick={getBaseNodeId}>
                <FaRedo />
              </ControlButton>
            </Controls>
            <Panel position='top-left'>
              <SpaceBetween direction='vertical' size='xs'>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Badge color='green'>-</Badge>
                  <span>Base node</span>
                </div>
              </SpaceBetween>
            </Panel>
            <Panel position='bottom-right'>
              <a
                href='https://w.amazon.com/bin/view/AWS_FinTech/DataFI/DataLattice/'
                target='_blank'
                rel='noopener noreferrer'
              >
                <img src={latticeLogo} alt='Data Lattice' style={{ width: '250px', height: '75px' }} />
              </a>
            </Panel>
          </ReactFlow>
        </div>
        {selectedNode && selectedLineageNodeComponent(selectedNode, closePanel, loadingSelectedNode)}
      </div>
    );
  }
};
